<!-- Поля одной строки заказ-наряда ремонта: работа, запчасть или материал. -->
<template>
  <div class="repair-item-row">
    <div class="repair-item-row__field repair-item-row__field--type">
      <base-label
        class="repair-item-row__label"
        :required="true"
        :filled="fieldIsFilled('item_type')"
      >
        Тип
      </base-label>
      <base-select
        :id="`repair_item_type_${item.local_id}`"
        :model-value="item.item_type"
        :options="itemTypeOptions"
        :clearable="false"
        :invalid="errors.item_type"
        @update:model-value="handleFieldChange('item_type', $event)"
      />
      <span v-if="errors.item_type" class="error-message">
        Выберите тип
      </span>
    </div>

    <div class="repair-item-row__field repair-item-row__field--name">
      <base-label
        class="repair-item-row__label"
        :required="true"
        :filled="fieldIsFilled('name')"
      >
        Название
      </base-label>
      <FleetRepairItemNameCombobox
        :id="`repair_item_name_${item.local_id}`"
        :model-value="item.name"
        :item-type="item.item_type"
        :item-type-options="itemTypeOptions"
        placeholder="Что сделали или купили"
        :invalid="errors.name"
        @lookup-change="handleCatalogLookupChange"
        @update:model-value="handleNameChange"
        @select="handleCatalogItemSelect"
      />
      <span v-if="errors.name" class="error-message">
        Укажите название
      </span>
    </div>

    <div
      v-if="showCatalogMeta"
      class="repair-item-row__catalog-meta"
    >
      <span
        class="repair-item-row__catalog-status"
        :class="`repair-item-row__catalog-status--${catalogStatusKind}`"
      >
        {{ catalogStatusText }}
      </span>

      <BaseCheckbox
        v-if="!item.catalog_item_id"
        :model-value="Boolean(item.create_catalog_item)"
        label="Добавить в каталог"
        @update:model-value="
          handleFieldChange('create_catalog_item', $event)
        "
      />

      <button
        v-else
        type="button"
        class="repair-item-row__catalog-clear"
        title="Очистить связь с каталогом"
        @click="clearCatalogItem"
      >
        <AppIcon name="close" :size="12" />
        Убрать связь
      </button>
    </div>

    <div class="repair-item-row__field repair-item-row__field--quantity">
      <span class="repair-item-row__label">Кол-во</span>
      <base-input
        :id="`repair_item_quantity_${item.local_id}`"
        :model-value="item.quantity"
        type="number"
        min="0"
        step="0.01"
        @update:model-value="handleAmountFieldChange('quantity', $event)"
      />
    </div>

    <div class="repair-item-row__field repair-item-row__field--unit">
      <span class="repair-item-row__label">Ед.</span>
      <base-input
        :id="`repair_item_unit_${item.local_id}`"
        :model-value="item.unit"
        placeholder="шт"
        @update:model-value="handleFieldChange('unit', $event)"
      />
    </div>

    <div class="repair-item-row__field repair-item-row__field--price">
      <span class="repair-item-row__label">Цена</span>
      <base-input
        :id="`repair_item_unit_price_${item.local_id}`"
        :model-value="item.unit_price"
        type="big_number"
        min="0"
        @update:model-value="handleAmountFieldChange('unit_price', $event)"
      />
    </div>

    <div class="repair-item-row__field repair-item-row__field--amount">
      <span class="repair-item-row__label">Сумма</span>
      <base-input
        :id="`repair_item_amount_${item.local_id}`"
        :model-value="item.amount"
        type="big_number"
        min="0"
        @update:model-value="handleFieldChange('amount', $event)"
      />
    </div>

    <button
      type="button"
      class="repair-item-row__remove"
      :disabled="!canRemove"
      title="Удалить строку"
      @click="$emit('remove', index)"
    >
      <AppIcon name="trash" :size="16" />
    </button>

    <div class="repair-item-row__details">
      <div class="repair-item-row__comment">
        <span class="repair-item-row__label">Пояснение</span>
        <base-textarea
          :id="`repair_item_description_${item.local_id}`"
          :model-value="item.description"
          placeholder="Детали по этой работе или запчасти"
          @update:model-value="handleFieldChange('description', $event)"
        />
      </div>

      <VatFields
        :vat-kind="item.vat_kind"
        :vat-rate="item.vat_rate"
        :vat-calculation-type="item.vat_calculation_type"
        :vat-kind-options="vatKindOptions"
        :vat-calculation-type-options="vatCalculationTypeOptions"
        :errors="errors"
        field-class="repair-item-row__field"
        label-class="repair-item-row__label"
        :vat-kind-filled="fieldIsFilled('vat_kind')"
        :vat-calculation-type-filled="
          fieldIsFilled('vat_calculation_type')
        "
        vat-kind-label="НДС"
        vat-rate-label="Ставка (%)"
        vat-rate-error-text="Укажите ставку НДС"
        vat-calculation-type-label="Расчёт НДС"
        vat-calculation-type-error-text="Выберите расчёт НДС"
        :use-base-label="false"
        @update:vatKind="handleFieldChange('vat_kind', $event)"
        @update:vatRate="handleFieldChange('vat_rate', $event)"
        @update:vatCalculationType="
          handleFieldChange('vat_calculation_type', $event)
        "
        @clear-error="handleVatClearError"
      />
    </div>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import FleetRepairItemNameCombobox from "@/components/Fleet/Repair/ItemNameCombobox.vue";
import VatFields from "@/components/Shared/VatFields.vue";
import { repairItemFieldIsFilled } from "@/utils/repairItemUtils";

export default {
  name: "FleetRepairItemForm",

  components: {
    AppIcon,
    FleetRepairItemNameCombobox,
    VatFields,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    itemTypeOptions: {
      type: Array,
      required: true,
    },
    vatKindOptions: {
      type: Array,
      required: true,
    },
    vatCalculationTypeOptions: {
      type: Array,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    canRemove: {
      type: Boolean,
      default: true,
    },
  },

  emits: [
    "clear-error",
    "remove",
    "sync-amount",
    "update-field",
  ],

  data() {
    return {
      catalogLookup: {
        hasExactMatch: false,
        isLoading: false,
        searchText: "",
      },
    };
  },

  computed: {
    hasManualName() {
      return String(this.item.name || "").trim().length > 0;
    },

    showCatalogMeta() {
      return Boolean(this.item.catalog_item_id) || this.hasManualName;
    },

    catalogStatusKind() {
      if (this.catalogLookup.isLoading) return "loading";
      if (this.item.catalog_item_id) return "linked";
      if (!this.item.create_catalog_item) return "empty";
      if (this.catalogLookup.hasExactMatch) return "info";

      return "success";
    },

    catalogStatusText() {
      if (this.catalogLookup.isLoading) return "Проверяем каталог";
      if (this.item.catalog_item_id) return "Из каталога";
      if (!this.item.create_catalog_item) return "Только в этом ремонте";
      if (this.catalogLookup.hasExactMatch) {
        return "Есть в каталоге, будет привязано";
      }

      return "Новая запись в каталог";
    },
  },

  methods: {
    fieldIsFilled(field) {
      return repairItemFieldIsFilled(this.item, field);
    },

    handleFieldChange(field, value) {
      this.$emit("update-field", {
        index: this.index,
        field,
        value,
      });
      this.$emit("clear-error", this.index, field);
    },

    handleNameChange(value) {
      this.handleFieldChange("name", value);
      this.handleFieldChange("catalog_item_id", null);
      this.handleFieldChange(
        "create_catalog_item",
        String(value || "").trim().length > 0
      );
    },

    handleAmountFieldChange(field, value) {
      this.handleFieldChange(field, value);
      this.$emit("sync-amount", this.index);
    },

    handleCatalogLookupChange(value) {
      this.catalogLookup = value;
    },

    handleVatClearError(field) {
      this.$emit("clear-error", this.index, field);
    },

    handleCatalogItemSelect(catalogItem) {
      if (!catalogItem) {
        this.clearCatalogItem();
        return;
      }

      [
        ["item_type", catalogItem.item_type],
        ["catalog_item_id", catalogItem.catalog_item_id],
        ["create_catalog_item", false],
        ["name", catalogItem.name],
        ["unit", catalogItem.unit || ""],
        ["unit_price", catalogItem.unit_price ?? ""],
        ["vat_kind", catalogItem.vat_kind || "no_vat"],
        ["vat_rate", catalogItem.vat_rate ?? ""],
        [
          "vat_calculation_type",
          catalogItem.vat_calculation_type || "included",
        ],
      ].forEach(([field, value]) => {
        this.handleFieldChange(field, value);
      });

      this.$emit("sync-amount", this.index);
    },

    clearCatalogItem() {
      this.handleFieldChange("catalog_item_id", null);
      this.handleFieldChange("create_catalog_item", false);
    },
  },
};
</script>

<style scoped>
.repair-item-row {
  display: grid;
  grid-template-columns: 150px minmax(220px, 1fr) 88px 82px 120px 120px 36px;
  gap: 10px;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #eef0f2;
}

.repair-item-row:last-child {
  border-bottom: none;
}

.repair-item-row__field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.repair-item-row__field--type {
  grid-column: 1;
  grid-row: 1;
}

.repair-item-row__field--name {
  grid-column: 2;
  grid-row: 1;
}

.repair-item-row__field--quantity {
  grid-column: 3;
  grid-row: 1;
}

.repair-item-row__field--unit {
  grid-column: 4;
  grid-row: 1;
}

.repair-item-row__field--price {
  grid-column: 5;
  grid-row: 1;
}

.repair-item-row__field--amount {
  grid-column: 6;
  grid-row: 1;
}

.repair-item-row__catalog-meta {
  display: inline-flex;
  align-items: center;
  grid-column: 2 / -2;
  grid-row: 2;
  min-height: 22px;
  gap: 12px;
  margin-top: -2px;
  color: #4b5563;
  font-size: 12px;
  line-height: 1.2;
}

.repair-item-row__catalog-status {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: #6b7280;
  white-space: nowrap;
}

.repair-item-row__catalog-status::before {
  display: block;
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9ca3af;
  content: "";
}

.repair-item-row__catalog-status--linked::before,
.repair-item-row__catalog-status--info::before {
  background: #2563eb;
}

.repair-item-row__catalog-status--success::before {
  background: #16a34a;
}

.repair-item-row__catalog-status--loading::before {
  background: #ca8a04;
}

.repair-item-row__catalog-meta :deep(.checkbox-label) {
  font-size: 12px;
  font-weight: 500;
}

.repair-item-row__catalog-clear {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  font-size: 12px;
}

.error-message {
  display: block;
  margin-top: 5px;
  color: var(--color-danger);
  font-size: 12px;
  line-height: 1.2;
}

.repair-item-row__label {
  display: none;
}

.repair-item-row__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  grid-column: 7;
  grid-row: 1;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #f3f4f6;
  color: var(--color-danger);
  cursor: pointer;
}

.repair-item-row__remove:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.repair-item-row__details {
  display: grid;
  grid-column: 1 / -1;
  grid-row: 3;
  grid-template-columns: minmax(260px, 1fr) 150px 110px 170px;
  gap: 10px;
  align-items: start;
}

.repair-item-row__details .repair-item-row__field {
  align-self: start;
}

.repair-item-row__details .repair-item-row__label {
  display: block;
  margin-bottom: 5px;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
}

.repair-item-row__comment :deep(.textarea) {
  min-height: 36px;
  max-height: 90px;
}

@media (max-width: 760px) {
  .repair-item-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: var(--base-in-indent);
    padding: 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
  }

  .repair-item-row__label {
    display: block;
    margin-bottom: 5px;
    color: #6b7280;
    font-size: 12px;
    font-weight: 600;
  }

  .repair-item-row__details {
    grid-column: auto;
    grid-row: auto;
    grid-template-columns: 1fr;
  }

  .repair-item-row__catalog-meta {
    grid-column: auto;
    grid-row: auto;
    flex-wrap: wrap;
    margin-top: 0;
  }

  .repair-item-row__remove {
    grid-column: auto;
    grid-row: auto;
    justify-self: end;
  }

  .repair-item-row__field--type,
  .repair-item-row__field--name,
  .repair-item-row__field--quantity,
  .repair-item-row__field--unit,
  .repair-item-row__field--price,
  .repair-item-row__field--amount {
    grid-column: auto;
    grid-row: auto;
  }
}
</style>
